import { useEffect, useMemo, useState } from 'react';
import {
  Area, AreaChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { ArrowDownTrayIcon, BookOpenIcon, SpeakerWaveIcon, UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import type { DailyAggregate } from '@dnyaneshwari/shared';
import { getDailyAggregates, mergeTopOvis, summarise, toCsv } from '@/services/analyticsService';

const PERIODS = [7, 30, 90] as const;

const minutes = (ms: number) => Math.round(ms / 60000);
const hours = (ms: number) => (ms / 3600000).toFixed(1);
const dayLabel = (d: string) => {
  try { return format(parseISO(d), 'd MMM'); } catch { return d; }
};

/**
 * What the app records, and nothing it does not. The app emits two event
 * types -- a read and a listen -- so this page reports those and says plainly
 * which of the aggregate's other fields are empty, rather than printing a
 * dash that reads like "nobody searched" when it means "nobody counted".
 */
export default function AnalyticsPage() {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);
  const [rows, setRows] = useState<DailyAggregate[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError('');
    getDailyAggregates(days)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [days]);

  const totals = useMemo(() => (rows ? summarise(rows) : null), [rows]);
  const topOvis = useMemo(() => (rows ? mergeTopOvis(rows) : []), [rows]);
  const chart = useMemo(
    () => (rows ?? []).map((r) => ({
      day: dayLabel(r.date),
      active: r.totalUsers,
      joined: r.newUsers,
    })),
    [rows]
  );

  function download() {
    if (!rows) return;
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dnyaneshwari-analytics-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const cards = totals
    ? [
        {
          label: 'Read or listened', value: totals.latest?.totalUsers ?? 0, icon: UsersIcon,
          color: 'bg-blue-50 text-blue-600',
          sub: totals.latest ? `on ${dayLabel(totals.latest.date)} · ${totals.avgActive}/day average` : '',
        },
        {
          label: 'New accounts', value: totals.newUsers.toLocaleString(), icon: UserPlusIcon,
          color: 'bg-green-50 text-green-600', sub: `over ${days} days`,
        },
        {
          label: 'Ovis read', value: totals.ovisRead.toLocaleString(), icon: BookOpenIcon,
          color: 'bg-amber-50 text-amber-600', sub: `${hours(totals.readingMs)} hours of reading`,
        },
        {
          label: 'Ovis listened', value: totals.ovisListened.toLocaleString(), icon: SpeakerWaveIcon,
          color: 'bg-purple-50 text-purple-600', sub: `${hours(totals.listeningMs)} hours of listening`,
        },
      ]
    : [];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            From the nightly aggregate — one document read per day shown, so this page costs {days} reads.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3 py-2 text-sm font-medium ${days === p ? 'bg-amber-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {p}d
              </button>
            ))}
          </div>
          <button
            onClick={download}
            disabled={!rows?.length}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />CSV
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
          Could not load the aggregates: {error}
        </p>
      )}

      {rows !== null && rows.length === 0 && !error && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-900">
          <p className="font-semibold mb-1">No aggregates yet.</p>
          <p>
            The <code>aggregateDaily</code> function writes one document per day at midnight IST, covering
            the day before. If the app has been live for more than a day and this is still empty, check the
            function's logs in the Firebase console.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {(rows === null ? [0, 1, 2, 3] : cards).map((c, i) =>
          typeof c === 'number' ? (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="h-4 w-28 bg-gray-100 rounded animate-pulse mb-4" />
              <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
            </div>
          ) : (
            <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">{c.label}</span>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{c.value}</p>
              {c.sub && <p className="text-xs text-gray-400 mt-1">{c.sub}</p>}
            </div>
          )
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-700">People who read or listened, by day</h3>
          {totals && totals.peakActive > 0 && (
            <span className="text-xs text-gray-400">peak {totals.peakActive}</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Someone who opened the app but did not read or play anything is not counted — the app records a
          read and a listen, nothing else.
        </p>
        <div className="h-72">
          {rows === null ? (
            <div className="h-full bg-gray-50 rounded-lg animate-pulse" />
          ) : rows.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">Nothing to plot yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="activeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d97706" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#d97706" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(v: number, n: string) => [v, n === 'active' ? 'Read or listened' : 'New accounts']}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(v) => (v === 'active' ? 'Read or listened' : 'New accounts')}
                />
                <Area type="monotone" dataKey="active" stroke="#d97706" strokeWidth={2} fill="url(#activeFill)" />
                <Line type="monotone" dataKey="joined" stroke="#2563eb" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Reading and listening · last {days} days</h3>
          {totals && (
            <div className="space-y-1">
              {[
                ['Ovis read', totals.ovisRead.toLocaleString()],
                ['Hours read', hours(totals.readingMs)],
                ['Average per ovi read', totals.ovisRead ? `${Math.round(totals.readingMs / totals.ovisRead / 1000)}s` : '—'],
                ['Ovis listened', totals.ovisListened.toLocaleString()],
                ['Hours listened', hours(totals.listeningMs)],
                ['Minutes per active day', totals.activeDaySum ? minutes((totals.readingMs + totals.listeningMs) / totals.activeDaySum) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <span className="text-sm text-gray-600">{label}</span>
                  <span className="text-sm font-semibold text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Most-read ovis</h3>
          <p className="text-xs text-gray-400 mb-3">Merged from each day's top ten.</p>
          {topOvis.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">No reads recorded in this period.</p>
          ) : (
            <div className="space-y-1">
              {topOvis.map(({ oviId, views }, i) => (
                <div key={oviId} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
                  <span className="w-6 text-xs font-bold text-gray-400">{i + 1}</span>
                  <span className="flex-1 text-sm font-mono text-gray-900">{oviId}</span>
                  <span className="text-sm font-semibold text-gray-700">{views.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* The honest part: what this page cannot tell you, and why. */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Not measured yet</h3>
        <p className="text-sm text-gray-600 mb-3">
          The app records exactly two things: an ovi being read and an ovi being played. Everything below is a
          field the nightly aggregate already has a slot for, but which nothing fills — so it would read zero
          whatever people actually did. Adding them is app-side work, listed for the next release.
        </p>
        <ul className="text-sm text-gray-600 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          {[
            'Sessions and app opens — startSession is never called',
            'Searches — no event on the search screen',
            'Shares — no event when an ovi is shared',
            'Downloads — no event when a part is saved',
            'Chapter views — so "most read chapter" is empty',
            'Platform split — the aggregate always writes zeros',
            'Retention and WAU/MAU — needs per-user day history',
            'Notification opens — needs the push tap handler',
          ].map((t) => (
            <li key={t} className="flex gap-2"><span className="text-gray-300">·</span>{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
